import React, { useState, useEffect } from "react";
import { OI_URL } from "../../config/config";
import { Modal, Button, Form, Upload, Select } from "antd";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "js-cookie";
import { ImportOutlined, InboxOutlined } from "@ant-design/icons";

const { Dragger } = Upload;
const { Option } = Select;

const ImportCartModal = (props) => {
    let { t, merchantList, refetch } = props;
    const [formData, setFormData] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedMerchantId, setSelectedMerchantId] = useState(null);
    const [form] = Form.useForm();

    const initFormData = {
        files: null,
        merchantId: null,
    };

    const layout = {
        labelCol: { span: 7 },
        wrapperCol: { span: 17 },
    };

    const formValInit = () => {
        setFormData(initFormData);
        setSelectedMerchantId(null);
    };

    const showModal = () => {
        formValInit();
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleOk = () => {
        onFinish();
        setIsSubmitting(true);
    };

    const isOkButtonProps = () => !formData || !formData.files || !selectedMerchantId;

    useEffect(() => {
        formValInit();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const formValChange = (chgV, allV) => {
        let newFormData = {...formData, ...allV };
        setFormData(newFormData);
        form.setFieldsValue(allV);
    };

    const handleMerchantChange = (value) => {
        setSelectedMerchantId(value);
        setFormData({ ...formData, merchantId: value });
    };

    const uploadConfig = {
        name: 'file',
        multiple: false,
        maxCount: 1,
        accept: '.json',
        beforeUpload(file) {
            setFormData({ ...formData, files: file });
            // Prevent default upload
            return false;
        },
        onRemove(e){
            let newFormData = {...formData, files: null };
            setFormData(newFormData);
        }
    };

    const onFinish = () => {
        if (!formData || !formData.files || !selectedMerchantId) {
            setIsSubmitting(false);
            return;
        }

        // Create FormData object for multipart/form-data
        const uploadFormData = new FormData();
        uploadFormData.append('files', formData.files);

        const apiUrl = OI_URL + `/api/v1/txshield/merchant/${selectedMerchantId}/transactionBatch`;
        axios
            .post(apiUrl, uploadFormData, { 
                headers: { 
                    Authorization: "Bearer " + Cookies.get("txfind_token"),
                } 
            })
            .then(({ data }) => {
                if (data.msg && data.results) {
                    const successCount = data.results.filter(result => result.status === "success").length;
                    const errorResults = data.results.filter(result => result.status === "error");
                    const totalCount = data.results.length;
                    
                    let message = `${data.msg}. ${successCount}/${totalCount} transactions processed successfully.`;
                    
                    if (errorResults.length > 0) {
                        const errorMessages = errorResults.map(error => 
                            `Transaction ${error.index + 1}: ${error.msg}`
                        ).join('\n');
                        message += `\n\nErrors:\n${errorMessages}`;
                    }
                    
                    Swal.fire({
                        icon: errorResults.length > 0 ? "warning" : "success",
                        title: errorResults.length > 0 ? "Partially Completed" : t("added"),
                        text: message,
                        showConfirmButton: true,
                        timer: 30000,
                        onClose: () => {
                            setIsSubmitting(false);
                            form.resetFields();
                            // Refetch the cart list to show updated data
                            if (refetch && successCount > 0) {
                                refetch();
                            }
                        },
                    });
                    handleCancel();
                } else {
                    Swal.fire({
                        icon: "error",
                        title: t("login.dialogs.catch"),
                        text: data.msg ? data.msg : "Unknown error occurred",
                        showConfirmButton: true,
                    });
                    setIsSubmitting(false);
                }
            })
            .catch((error) => {
                Swal.fire({
                    icon: "error",
                    title: t("login.dialogs.catch"),
                    text: error.response?.data?.msg || "",
                    showConfirmButton: true,
                });
                setIsSubmitting(false);
            });
    }

    return (
        <>
            <Button key={`btn_import`} type="link" onClick={showModal}>
                <div className="d-flex align-items-center">
                    <ImportOutlined style={{ fontSize: "18px" }} /> <div style={{ marginLeft: "4px" }}>Import JSON</div>
                </div>
            </Button>
            <Modal
                title={t("contents.oi.form.cartImport")}
                width={600}
                visible={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                footer={[
                    <Button key="back" onClick={() => handleCancel()}>
                        {t("contents.chargebacks.form.cancel")}
                    </Button>,
                    <Button key="submit" type="primary" loading={isSubmitting} disabled={isOkButtonProps()} onClick={() => handleOk()}>
                        {isSubmitting ? t("contents.chargebacks.form.submitting") : t("contents.chargebacks.form.submit")}
                    </Button>,
                ]}
            >
                <Form form={form} {...layout} name="importcart" onValuesChange={formValChange}>
                    <a 
                        href="https://docs.payshield.ai/txfind-payreveal-order-insight-ican/merchant/upload-transaction-batch-file" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ display: "block", marginBottom: 10, textDecoration: "underline" }}
                    >
                        View API Documentation & File Format
                    </a>
                    <Dragger {...uploadConfig}>
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Click or drag file to this area to upload</p>
                        <p className="ant-upload-hint">
                            Upload JSON file containing cart transaction data. Please ensure the file follows the format specified in the API documentation.
                        </p>
                    </Dragger>
                    <Form.Item 
                        name="merchantId" 
                        label={t("contents.oi.form.merchant")} 
                        rules={[{ required: true }]}
                        style={{ marginTop: 18 }}
                    >
                        <Select placeholder={t("contents.merchants.form.please_select")} onChange={handleMerchantChange}>
                            {merchantList && merchantList.map((item) => (
                                <Option key={item.merchantId} value={item.merchantId}>
                                    {item.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default ImportCartModal; 